
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedRatingTable,
    IGroupedRatingTableRowProps,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FunctionComponent, useContext, useMemo } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { useTranslation } from 'react-i18next';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IEORTCIL353FormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */


const ratings: IGroupedRatingTableRowProps[] = [
    {
        propertyPath: 'restNeeded'
    },
    {
        propertyPath: 'feltWeak'
    },
    {
        propertyPath: 'feltTired'
    },
    {
        propertyPath: 'experiencedFinancialDifficulties'
    },
    {
        propertyPath: 'feltLessAttractive'
    },
    {
        propertyPath: 'feltLessFeminine'
    },
    {
        propertyPath: 'difficultyViewingNakedSelf'
    },
    {
        propertyPath: 'dissatisifiedWithBody'
    },
]

const EORTCIL353Form: FunctionComponent<IEORTCIL353FormProps> = () => {
    const { t } = useTranslation();

    const overThePastWeek = useMemo(() => {
        const translated = t("forms.eortcil353.overThePastWeek", {defaultValue: null});

        if(translated == "forms.eortcil353.overThePastWeek") {
            return null;
        }

        return translated;
    }, [t]);

    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey={"forms.eortcil353"}
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                { 
                    overThePastWeek ? 
                    <Box
                        sx={{
                            padding: theme.spacing(3, 0, 3, 3)
                        }}
                    >
                        <Typography variant={'h2'}>{overThePastWeek}</Typography>
                    </Box> :
                    null 
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="restNeeded"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="restNeeded"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="feltWeak"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="feltWeak"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="feltTired"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="feltTired"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="experiencedFinancialDifficulties"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="experiencedFinancialDifficulties"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="feltLessAttractive"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="feltLessAttractive"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="feltLessFeminine"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="feltLessFeminine"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="difficultyViewingNakedSelf"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="difficultyViewingNakedSelf"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="dissatisifiedWithBody"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="dissatisifiedWithBody"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    !showSteps && (
                        <div>    
                            <GroupedRatingTable 
                                rows={ratings}
                                TableProps={{
                                    sx: {
                                        tableLayout: 'fixed'
                                    }
                                }}
                                LabelTableHeadCellProps={{
                                    sx: {
                                        width: '100%'
                                    }
                                }}
                                ValueTableHeadCellProps={{
                                    sx: {
                                        width: 110
                                    }
                                }}
                            />
                        </div>
                    )
                }
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default EORTCIL353Form;
